/**
 * Itinerary Converter - Convert backend ItineraryDTO to frontend Activity
 */

import type { Activity } from '../types/activity';
import type { ItineraryDTO } from '../types/trip';

/**
 * 從 ISO 時間字串提取 HH:mm 格式
 * @param timeString - yyyy-MM-dd HH:mm:ss 格式的時間字串
 * @returns HH:mm 格式的時間或 null
 */
function extractTime(timeString: string | null): string | null {
  if (!timeString) return null;

  // 從 "yyyy-MM-dd HH:mm:ss" 提取 "HH:mm"
  const match = timeString.match(/(\d{2}):(\d{2}):\d{2}$/);
  if (match) {
    return `${match[1]}:${match[2]}`;
  }

  return null;
}

/**
 * 將後端 ItineraryDTO 轉換為前端 Activity
 * @param itinerary - 後端行程資料
 * @param tripId - 旅程 ID
 * @returns 前端 Activity 格式
 */
export function convertItineraryToActivity(
  itinerary: ItineraryDTO,
  tripId: string
): Activity {
  return {
    id: itinerary.itineraryId,
    tripId: tripId,
    name: itinerary.title,
    location: itinerary.locationName || '',
    // daySequence 是 1-based，需要轉換為 0-based dayIndex
    dayIndex: itinerary.daySequence != null ? itinerary.daySequence - 1 : null,
    // orderInDay 後端已經是從適當的數字開始，前端使用相同的值
    order: itinerary.orderInDay,
    scheduledTime: extractTime(itinerary.startTime),
    startTime: extractTime(itinerary.startTime),
    endTime: extractTime(itinerary.endTime),
    note: itinerary.note || null,
    type: itinerary.type,
    lat: itinerary.latitude ?? null,
    lng: itinerary.longitude ?? null,
    address: itinerary.address ?? null,
    placeId: itinerary.placeId ?? null,
    photoReference: itinerary.photoReference ?? null,
    isPrimary: itinerary.isPrimary ?? false,
    createdAt: new Date(),
    updatedAt: new Date(),
  };
}

/**
 * 將行程列表按照 daySequence 分組
 * @param itineraries - 後端行程列表
 * @param tripId - 旅程 ID
 * @returns 按天數分組的 Activity 陣列，key 為 dayIndex (0-based)
 */
export function groupItinerariesByDay(
  itineraries: ItineraryDTO[],
  tripId: string
): Map<number, Activity[]> {
  const grouped = new Map<number, Activity[]>();

  itineraries.forEach((itinerary) => {
    if (itinerary.daySequence === null) return; // 跳過未排程的行程

    const activity = convertItineraryToActivity(itinerary, tripId);
    const dayIndex = activity.dayIndex!;

    if (!grouped.has(dayIndex)) {
      grouped.set(dayIndex, []);
    }

    grouped.get(dayIndex)!.push(activity);
  });

  // 每一天的行程按照 orderInDay 排序
  grouped.forEach((activities) => {
    activities.sort((a, b) => {
      if (a.order === null || b.order === null) return 0;
      return a.order - b.order;
    });
  });

  return grouped;
}

/**
 * 將未排程的行程轉換為 Activity 列表
 * @param itineraries - 後端未排程行程列表
 * @param tripId - 旅程 ID
 * @returns Activity 陣列
 */
export function convertUnplannedItineraries(
  itineraries: ItineraryDTO[],
  tripId: string
): Activity[] {
  return itineraries.map((itinerary) =>
    convertItineraryToActivity(itinerary, tripId)
  );
}
